import React, { useState, useEffect, useRef } from 'react';
// FIX: Corrected Firebase User type usage.
import firebase from 'firebase/compat/app';
import { Unsubscribe } from 'firebase/firestore';
import { auth, functions, firebaseError } from './services/firebase';
import * as firestoreService from './services/firestore';
import * as XLSX from 'xlsx';
import { Type } from "@google/genai";
import { geminiProxy } from './services/geminiProxy';
import { captureError, setUserContext, clearUserContext } from './services/errorTracking';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import DashboardView from './components/DashboardView';
import DataView from './components/DataView';
import TimeLogView from './components/TimeLogView';
import SettingsView from './components/SettingsView';
import LoginView from './components/LoginView';
import MonthlyAnalysisView from './components/MonthlyAnalysisView';
import OnboardingWizard from './components/OnboardingWizard';
import TransactionModal from './components/TransactionModal';
import VoiceCaptureModal from './components/VoiceCaptureModal';
import ExportModal from './components/ExportModal';
import CategoryView from './components/CategoryView';
import ProfileModal from './components/ProfileModal';
import CategorizationView from './components/CategorizationView';
import LegalModal from './components/LegalModal';
import RuleModal from './components/RuleModal';
import TourGuide from './components/TourGuide';
import VerificationView from './components/VerificationView';
import DeleteAccountModal from './components/DeleteAccountModal';
import HelpView from './components/HelpView';
import SuggestionsView from './components/SuggestionsView';
import TrialBanner from './components/TrialBanner';
import SubscriptionWall from './components/SubscriptionWall';
import DuesView from './components/DuesView';
import DuesModal from './components/DuesModal';
import SubscriptionsView from './components/SubscriptionsView';
import SubscriptionModal from './components/SubscriptionModal';
import { PrivacyPolicyContent, TermsContent, LegalNoticeContent, CookiesPolicyContent } from './components/legalContent';
import PricingModal from './components/PricingModal'; // Import the new modal
import VersionModal from './components/VersionModal';
import { Page, Transaction, TimeEntry, Rule, ProrationRule, Settings, Due, Subscription } from './types';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const initialCategories = ["Ingresos Ventas", "Tienda Online", "Proveedores", "Ingresos Emprendimiento", "Marketing", "Alquiler", "Casa", "Ingresos Trabajo", "Compras", "YouTube", "Ocio", "Sueldos", "Mascotas", "Familia", "Transferencias", "Desconocida"];
const defaultSettings: Settings = { categories: initialCategories, rules: [], prorationRules: [], currency: 'EUR', onboardingCompleted: false, showTourGuide: true, subscriptionStatus: 'free' };

type Theme = 'light' | 'dark' | 'system';
type AccessStatus = 'loading' | 'granted' | 'denied_trial_ended' | 'denied_canceled' | 'denied_past_due';

const App: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<firebase.User | null>(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [currentPage, setCurrentPage] = useState<Page>('dashboard');
  const [theme, setTheme] = useState<Theme>((localStorage.getItem('theme') as Theme) || 'system');
  const [accessStatus, setAccessStatus] = useState<AccessStatus>('loading');
  
  // --- AI State ---
  const [aiEnabled] = useState<boolean>(true);
  const [apiKeyError] = useState<string | null>(null);

  // --- Cloud-synced State ---
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [dues, setDues] = useState<Due[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [settings, setSettings] = useState<Settings>(defaultSettings);
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 1024);

  // State for modals
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [isDuesModalOpen, setIsDuesModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [isVoiceModalOpen, setIsVoiceModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isLegalModalOpen, setIsLegalModalOpen] = useState(false);
  const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false); // Add state for pricing modal
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false);
  const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);
  const [showTour, setShowTour] = useState(false);
  
  // State for categorization review
  const [isReviewingCategorization, setIsReviewingCategorization] = useState(false);
  const [initialTransactionFilter, setInitialTransactionFilter] = useState<'all' | 'income' | 'expense' | null>(null);
  
  // State for voice input flow
  const [voiceInputText, setVoiceInputText] = useState<string | null>(null);
  const [voiceInputTextForDue, setVoiceInputTextForDue] = useState<string | null>(null);
  const [voiceInputTextForSubscription, setVoiceInputTextForSubscription] = useState<string | null>(null);


  // State for editing/export
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [editingDue, setEditingDue] = useState<Due | null>(null);
  const [editingSubscription, setEditingSubscription] = useState<Subscription | null>(null);
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [exportFormat, setExportFormat] = useState<'Excel' | 'PDF'>('Excel');
  
  // State for XLS import
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isCategorizing, setIsCategorizing] = useState(false);
  
  // State for legal modal content
  const [legalModalContent, setLegalModalContent] = useState<{ title: string; content: React.ReactNode } | null>(null);

  useEffect(() => {
    localStorage.setItem('theme', theme);
    const root = window.document.documentElement;
    
    if (theme === 'system') {
        const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        root.classList.remove('light', 'dark');
        root.classList.add(systemTheme);
    } else {
        root.classList.remove('light', 'dark');
        root.classList.add(theme);
    }
  }, [theme]);
  
  useEffect(() => {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => {
          if (theme === 'system') {
              const newSystemTheme = mediaQuery.matches ? 'dark' : 'light';
              window.document.documentElement.classList.remove('light', 'dark');
              window.document.documentElement.classList.add(newSystemTheme);
          }
      };
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);


  useEffect(() => {
    if (auth) {
        const unsubscribe = auth.onAuthStateChanged((user) => {
          setCurrentUser(user as firebase.User | null);
          setLoadingAuth(false);
          if (user) {
            setUserContext(user.uid, user.email || undefined);
          } else {
            clearUserContext();
          }
        });
        return () => unsubscribe();
    } else {
        setLoadingAuth(false);
    }
  }, []);

  // Optimistic UI update after successful subscription payment
  useEffect(() => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('subscription_success')) {
          // Limpiar la URL — el estado real de suscripción viene del webhook
          // de Stripe via Firestore (fuente de verdad), no de parámetros URL.
          window.history.replaceState({}, document.title, window.location.pathname);
      }
  }, []);
  
  // --- Firestore Data Sync ---
  useEffect(() => {
    if (currentUser && currentUser.emailVerified) {
        const unsubscribers: Unsubscribe[] = [];
        
        const listenToData = async () => {
            unsubscribers.push(firestoreService.listenToCollection(currentUser.uid, 'transactions', (data) => setTransactions(data as Transaction[])));
            unsubscribers.push(firestoreService.listenToCollection(currentUser.uid, 'timeEntries', (data) => setTimeEntries(data as TimeEntry[])));
            unsubscribers.push(firestoreService.listenToCollection(currentUser.uid, 'dues', (data) => setDues(data as Due[])));
            unsubscribers.push(firestoreService.listenToCollection(currentUser.uid, 'subscriptions', (data) => setSubscriptions(data as Subscription[])));

            
            // Listen to settings (categories, rules, etc.)
            unsubscribers.push(firestoreService.listenToSettings(currentUser.uid, (newSettings) => {
                setSettings(newSettings);
            }, defaultSettings));
        };
        
        listenToData();

        return () => {
            unsubscribers.forEach(unsub => unsub());
        };
    } else if (!currentUser) {
        // Clear all data on logout
        setTransactions([]);
        setTimeEntries([]);
        setDues([]);
        setSubscriptions([]);
        setSettings(defaultSettings);
        setAccessStatus('loading'); // Reset access status on logout
    }
  }, [currentUser]);
  
  // --- Subscription Access Control ---
  useEffect(() => {
    if (loadingAuth || !currentUser || !currentUser.emailVerified) {
        // Don't run this check until user is fully authenticated and verified
        return;
    }
    
    if (!settings.onboardingCompleted) {
      // Don't run check until onboarding is done, access granted implicitly.
      setAccessStatus('granted');
      return;
    }
    
    const status = settings.subscriptionStatus;
    
    if (status === 'active') {
      setAccessStatus('granted');
    } else if (status === 'trial' && settings.trialEndDate) {
      const trialEndDate = new Date(settings.trialEndDate);
      if (new Date() < trialEndDate) {
        setAccessStatus('granted');
      } else {
        setAccessStatus('denied_trial_ended');
      }
    } else if (status === 'canceled') {
      setAccessStatus('denied_canceled');
    } else if (status === 'past_due') {
      setAccessStatus('denied_past_due');
    } else {
      // Default case for 'free' or undefined status after onboarding.
      // Treat as if trial has ended.
      setAccessStatus('denied_trial_ended');
    }

  }, [settings, loadingAuth, currentUser]);


  // Effect to trigger the tour guide based on user settings.
  useEffect(() => {
    // Show tour if onboarding is done AND the setting is enabled.
    // This will run every time the user logs in and settings are loaded.
    if (settings.onboardingCompleted && settings.showTourGuide) {
        setShowTour(true);
    }
  }, [settings.onboardingCompleted, settings.showTourGuide]);


  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1024) {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const handlePageChange = (page: Page) => {
    setCurrentPage(page);
    setIsReviewingCategorization(false); // Exit review mode when changing page
    if (window.innerWidth <= 1024) {
        setIsSidebarOpen(false);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  
  const handleOpenLegalModal = (type: 'privacy' | 'terms' | 'legal' | 'cookies') => {
    switch (type) {
        case 'privacy':
            setLegalModalContent({ title: "Política de Privacidad", content: <PrivacyPolicyContent /> });
            break;
        case 'terms':
            setLegalModalContent({ title: "Términos de Uso", content: <TermsContent /> });
            break;
        case 'legal':
            setLegalModalContent({ title: "Aviso Legal", content: <LegalNoticeContent /> });
            break;
        case 'cookies':
            setLegalModalContent({ title: "Política de Cookies", content: <CookiesPolicyContent /> });
            break;
    }
    setIsLegalModalOpen(true);
  };
  
  const handleOnboardingComplete = async (config: { currency: string, profile: string, alias: string }) => {
    if (!currentUser) return;

    // --- Prepare all the data first ---
    const baseCategories = ["Ingresos", "Transferencias", "Ocio", "Compras", "Casa", "Transporte", "Desconocida"];
    const profileCategories: Record<string, string[]> = {
        personal: [],
        work: ["Trabajo", "Proveedores", "Marketing", "Software", "Impuestos"],
        family: ["Familia", "Hijos", "Educación", "Salud"],
        all: ["Trabajo", "Proveedores", "Marketing", "Software", "Impuestos", "Familia", "Hijos", "Educación", "Salud"]
    };
    const newCategories = [...new Set([...baseCategories, ...(profileCategories[config.profile] || [])])];
    const trialEndDate = new Date();
    trialEndDate.setDate(trialEndDate.getDate() + 30); // 30-day trial

    const newSettings: Settings = {
        ...settings,
        currency: config.currency,
        categories: newCategories.sort(),
        onboardingCompleted: true,
        showTourGuide: true, // Always enable tour after onboarding
        subscriptionStatus: 'trial',
        trialEndDate: trialEndDate.toISOString(),
    };
    
    // --- Perform Optimistic UI update FIRST ---
    // This makes the UI feel instant and prevents it from getting stuck.
    setSettings(newSettings);
    
    // --- Then, sync with the backend ---
    try {
        // The onAuthStateChanged listener will handle updating the user object in state.
        await currentUser.updateProfile({ displayName: config.alias });
        const initializeUser = functions!.httpsCallable("initializeUserSettings");
        await initializeUser({ settings: newSettings });
    } catch (error) {
        captureError(error, { action: "onboarding_save" });
        // Optionally, you could add logic here to revert the UI state change,
        // but for now, an alert is sufficient to inform the user.
        alert("Hubo un error al guardar tu configuración. Por favor, recarga la página.");
    }
};


  // --- AI Categorization Logic ---
  const applyCategorizationRules = (concept: string, counterparty: string): string[] => {
      const lowerCaseConcept = concept.trim().toLowerCase();
      // First, check user-defined rules (now with 'includes')
      for (const rule of settings.rules) {
          if (lowerCaseConcept.includes(rule.concept.trim().toLowerCase())) {
              return [rule.category];
          }
      }
      // Then, check hardcoded rules as a fallback
      if (concept.toLowerCase().includes("google *ads")) return ["Marketing"];
      if (concept.toLowerCase().includes("stripe payout")) return ["Ingresos Ventas"];
      if (counterparty.toLowerCase().includes("systeme.io")) return ["Ingresos Trabajo"];
      if (counterparty.toLowerCase().includes("amazon")) return ["Compras"];
      if (concept.toLowerCase().includes("alquiler")) return ["Alquiler", "Casa"];
      return ["Desconocida"];
  }

  // This function is now used by CategorizationView
  const getCategoryFromAI = async (concept: string, counterparty: string): Promise<string[]> => {
      if (!aiEnabled) return ["Desconocida"]; // Graceful failure if AI is not initialized
      try {
          const response = await geminiProxy.generateContent(
              `Dada esta lista de categorías: ${settings.categories.filter(c => c !== "Desconocida").join(', ')}. ¿Cuál es la categoría más apropiada para una transacción con el concepto "${concept}" y contrapartida "${counterparty}"? Devuelve solo el nombre de la categoría en formato JSON {"category": "nombre_categoria"}.`,
              {
                  responseMimeType: "application/json",
                  responseSchema: {
                      type: Type.OBJECT,
                      properties: {
                          category: {
                              type: Type.STRING,
                              description: "The most appropriate category from the provided list.",
                          }
                      },
                      required: ["category"],
                  }
              },
          );
          
          const jsonText = response.text.trim();
          const jsonResponse = JSON.parse(jsonText);
          const suggestedCategory = jsonResponse.category;

          if (suggestedCategory && settings.categories.includes(suggestedCategory)) {
              return [suggestedCategory];
          }
          return ["Desconocida"];
      } catch (error) {
          captureError(error, { action: "ai_categorization" });
          return ["Desconocida"]; // Fallback on error
      }
  };


  // --- Handlers ---
  const handleOpenAddModal = () => {
    setEditingTransaction(null);
    setIsTransactionModalOpen(true);
  };
  
  const handleOpenEditModal = (transaction: Transaction) => {
    setEditingTransaction(transaction);
    setIsTransactionModalOpen(true);
  };

  const handleCloseTransactionModal = () => {
    setIsTransactionModalOpen(false);
    setEditingTransaction(null);
    setVoiceInputText(null); // Clear voice text when closing
  };

  const handleVoiceAccept = (text: string) => {
      setIsVoiceModalOpen(false);
      if (currentPage === 'dues') {
          setVoiceInputTextForDue(text);
          setIsDuesModalOpen(true);
      } else if (currentPage === 'subscriptions') {
          setVoiceInputTextForSubscription(text);
          setIsSubscriptionModalOpen(true);
      } else {
          setVoiceInputText(text); // Store the text
          setIsTransactionModalOpen(true); // Open the transaction modal for AI processing
      }
  };

  const handleSaveTransaction = async (transactionData: Omit<Transaction, 'id' | 'balance'>, id?: string) => {
      if (!currentUser) return;
      
      const newCategory = transactionData.category[0];
      if (newCategory && !settings.categories.includes(newCategory)) {
        await handleAddCategory(newCategory);
      }
      
      if (id) { // Editing existing transaction
          await firestoreService.updateDocument(currentUser.uid, 'transactions', id, transactionData);
      } else { // Adding new transaction
          const newTransaction: Omit<Transaction, 'id'> = {
              ...transactionData,
              balance: 0, 
          };
          await firestoreService.addDocument(currentUser.uid, 'transactions', newTransaction);
      }
      handleCloseTransactionModal();
  };
  
  const handleDeleteTransaction = async (id: string) => {
    if (!currentUser) return;
    await firestoreService.deleteDocument(currentUser.uid, 'transactions', id);
  };
  
  // --- Dues Handlers ---
  const handleOpenAddDueModal = () => {
    setEditingDue(null);
    setIsDuesModalOpen(true);
  };
  
  const handleOpenEditDueModal = (due: Due) => {
    setEditingDue(due);
    setIsDuesModalOpen(true);
  };

  const handleCloseDueModal = () => {
    setIsDuesModalOpen(false);
    setEditingDue(null);
    setVoiceInputTextForDue(null);
  };

  const handleSaveDue = async (dueData: Omit<Due, 'id'>, id?: string) => {
      if (!currentUser) return;
      
      const newCategory = dueData.category;
      if (newCategory && !settings.categories.includes(newCategory)) {
        await handleAddCategory(newCategory);
      }
      
      if (id) {
          await firestoreService.updateDocument(currentUser.uid, 'dues', id, dueData);
      } else {
          await firestoreService.addDocument(currentUser.uid, 'dues', dueData);
      }
      handleCloseDueModal();
  };

  const handleDeleteDue = async (id: string) => {
    if (!currentUser) return;
    await firestoreService.deleteDocument(currentUser.uid, 'dues', id);
  };

  // --- Subscription Handlers ---
  const handleOpenAddSubscriptionModal = () => {
    setEditingSubscription(null);
    setIsSubscriptionModalOpen(true);
  };

  const handleOpenEditSubscriptionModal = (sub: Subscription) => {
    setEditingSubscription(sub);
    setIsSubscriptionModalOpen(true);
  };

  const handleCloseSubscriptionModal = () => {
    setIsSubscriptionModalOpen(false);
    setEditingSubscription(null);
    setVoiceInputTextForSubscription(null);
  };

  const handleSaveSubscription = async (subData: Omit<Subscription, 'id'>, id?: string) => {
      if (!currentUser) return;
      
      const newCategory = subData.category;
      if (newCategory && !settings.categories.includes(newCategory)) {
        await handleAddCategory(newCategory);
      }
      
      if (id) {
          await firestoreService.updateDocument(currentUser.uid, 'subscriptions', id, subData);
      } else {
          await firestoreService.addDocument(currentUser.uid, 'subscriptions', subData);
      }
      handleCloseSubscriptionModal();
  };

  const handleDeleteSubscription = async (id: string) => {
    if (!currentUser) return;
    await firestoreService.deleteDocument(currentUser.uid, 'subscriptions', id);
  };
  
  const handleTriggerImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!currentUser) return;
      const file = e.target.files?.[0];
      if (!file) return;
      
      const fileName = file.name;
      setIsCategorizing(true);
      const reader = new FileReader();
      
      reader.onload = async (event) => {
          try {
              const data = event.target?.result;
              const workbook = XLSX.read(data, { type: 'array', cellDates: true });
              const sheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[sheetName];
              const json: any[] = XLSX.utils.sheet_to_json(worksheet);

              if (json.length === 0) {
                  throw new Error("El archivo Excel está vacío o no tiene datos válidos en la primera hoja.");
              }

              const headerMap: { [key: string]: string[] } = {
                  concept: ['concepto', 'descripción', 'description', 'movimiento', 'details', 'detalle', 'operations'],
                  amount: ['importe', 'importe (€)', 'amount', 'cantidad', 'valor', 'crédito', 'débito', 'credit', 'debit', 'movement'],
                  date: ['fecha valor', 'f. valor', 'value date', 'fecha operación', 'fecha', 'date', 'operation date'],
                  counterparty: ['contrapartida', 'counterparty', 'ordenante', 'source', 'origen'],
                  category: ['categoría', 'categoria', 'category', 'tag']
              };
              
              let categoryFromFileName: string | null = null;
              if (aiEnabled) {
                try {
                    const response = await geminiProxy.generateContent(
                        `Dada esta lista de categorías: ${settings.categories.filter(c => c !== "Desconocida").join(', ')}. ¿Cuál es la categoría más apropiada para un archivo con el nombre "${fileName}"? Si no estás seguro, responde con "Desconocida". Devuelve solo el nombre de la categoría en formato JSON {"category": "nombre_categoria"}.`,
                        {
                            responseMimeType: "application/json",
                            responseSchema: {
                                type: Type.OBJECT,
                                properties: {
                                    category: { type: Type.STRING }
                                },
                                required: ["category"]
                            }
                        },
                    );
                    const jsonResponse = JSON.parse(response.text.trim());
                    if (jsonResponse.category && settings.categories.includes(jsonResponse.category)) {
                        categoryFromFileName = jsonResponse.category;
                    }
                } catch (error) {
                    console.warn("AI couldn't determine category from filename:", error);
                }
              }

              const newTransactions = json.map((row) => {
                   const normalizeKey = (key: string) => key.trim().toLowerCase().replace(/[^a-z0-9]/gi, '');
                
                  const getValue = (obj: any, keys: string[]): any => {
                      const normalizedObjKeys: { [key: string]: string } = {};
                      for (const rowKey in obj) {
                          normalizedObjKeys[normalizeKey(rowKey)] = rowKey;
                      }

                      for (const key of keys) {
                          const normalizedKey = normalizeKey(key);
                          if (normalizedObjKeys[normalizedKey]) {
                              return obj[normalizedObjKeys[normalizedKey]];
                          }
                      }
                      return undefined;
                  };

                  const concept = getValue(row, headerMap.concept) || 'N/A';
                  
                  const creditValue = getValue(row, ['crédito', 'credit']);
                  const debitValue = getValue(row, ['débito', 'debit']);
                  let amount;
                  if (creditValue !== undefined || debitValue !== undefined) {
                      const credit = typeof creditValue === 'string' ? parseFloat(creditValue.replace(',', '.')) : parseFloat(creditValue || '0');
                      const debit = typeof debitValue === 'string' ? parseFloat(debitValue.replace(',', '.')) : parseFloat(debitValue || '0');
                      amount = (isNaN(credit) ? 0 : credit) - (isNaN(debit) ? 0 : debit);
                  } else {
                      const amountValue = getValue(row, headerMap.amount);
                      amount = typeof amountValue === 'string' ? parseFloat(amountValue.replace(',', '.')) : parseFloat(amountValue);
                  }
                  
                  const rawDate = getValue(row, headerMap.date);
                  const counterparty = getValue(row, headerMap.counterparty) || 'N/A';
                  const excelCategory = getValue(row, headerMap.category);

                  if (!rawDate || isNaN(amount)) return null;

                  let transactionDate: string;
                  if (rawDate instanceof Date) {
                      transactionDate = rawDate.toISOString().split('T')[0];
                  } else if (typeof rawDate === 'string' && rawDate.length > 0) {
                       // Handle DD/MM/YYYY and other formats flexibly
                      const parts = rawDate.split(/[/.-]/);
                      let dateObj;
                      if (parts.length === 3) {
                          // Assuming DD/MM/YYYY if separators are used
                          if (parts[2].length === 4) {
                             dateObj = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
                          } else { // Assuming MM/DD/YYYY
                             dateObj = new Date(`${parts[2]}-${parts[0]}-${parts[1]}`);
                          }
                      } else {
                          dateObj = new Date(rawDate);
                      }
                      transactionDate = !isNaN(dateObj.getTime()) ? dateObj.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
                  } else {
                      return null;
                  }
                  
                  let category: string[];
                  if (excelCategory && typeof excelCategory === 'string' && excelCategory.trim() !== '') {
                      const importedCat = excelCategory.trim();
                      const existingCategory = settings.categories.find(c => c.toLowerCase() === importedCat.toLowerCase());
                      
                      if (existingCategory) {
                          category = [existingCategory];
                      } else {
                          category = ["Desconocida"];
                      }
                  } else {
                      category = applyCategorizationRules(concept, counterparty);
                       if (category[0] === 'Desconocida' && categoryFromFileName) {
                        category = [categoryFromFileName];
                      }
                  }
                  
                  return {
                      date: transactionDate,
                      concept,
                      counterparty,
                      amount,
                      balance: 0,
                      category,
                  };
              }).filter(Boolean) as Omit<Transaction, 'id'>[];

              
              if (newTransactions.length === 0) {
                  alert("No se encontraron transacciones válidas en el archivo. Por favor, revisa que las columnas tengan nombres como 'Fecha', 'Concepto' e 'Importe'.");
              } else {
                  await firestoreService.addMultipleDocuments(currentUser.uid, 'transactions', newTransactions);
                  alert(`${newTransactions.length} transacciones importadas correctamente.`);
                  const hasUncategorized = newTransactions.some(t => t.category[0] === 'Desconocida');
                  if (hasUncategorized) {
                    setIsReviewingCategorization(true);
                    setCurrentPage('dashboard');
                  }
              }

          } catch (error: any) {
              captureError(error, { action: "excel_import" });
              alert(`Error al procesar el archivo: ${error.message || 'Por favor, comprueba el formato del Excel.'}`);
          } finally {
               setIsCategorizing(false);
          }
      };
      reader.readAsArrayBuffer(file);
      e.target.value = ''; // Reset file input
  };
  
  // --- Export Logic ---
  const handleOpenExportModal = (format: 'Excel' | 'PDF') => {
      setExportFormat(format);
      setIsExportModalOpen(true);
  };

  const handleCloseExportModal = () => {
      setIsExportModalOpen(false);
  };
  
  const handleExportData = (options: { format: 'Excel' | 'PDF', dateRange: { start: string, end: string }, type: 'all' | 'income' | 'expense' }) => {
    const { format, dateRange, type } = options;

    const filteredData = transactions.filter(t => {
      const transactionDate = new Date(t.date);
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      endDate.setHours(23, 59, 59, 999); 

      const inDateRange = transactionDate >= startDate && transactionDate <= endDate;
      if (!inDateRange) return false;
      
      if (type === 'income') return t.amount > 0;
      if (type === 'expense') return t.amount < 0;
      return true;
    });
    
    if(filteredData.length === 0) {
        alert("No hay datos para exportar en el rango de fechas y tipo seleccionado.");
        return;
    }

    const dataToExport = filteredData.map(t => ({
      'Fecha': t.date,
      'Concepto': t.concept,
      'Contrapartida': t.counterparty,
      'Importe': t.amount,
      'Categorías': t.category.join(', '),
    }));

    const fileName = `MisCuentas360_Export_${new Date().toISOString().split('T')[0]}`;

    if (format === 'Excel') {
        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Transacciones");
        XLSX.writeFile(workbook, `${fileName}.xlsx`);
    } else if (format === 'PDF') {
        const doc = new jsPDF();
        doc.text("Informe de Transacciones", 14, 16);
        autoTable(doc, {
            head: [['Fecha', 'Concepto', 'Contrapartida', 'Importe', 'Categorías']],
            body: dataToExport.map(item => Object.values(item)),
            startY: 22,
            headStyles: { fillColor: [59, 130, 246] },
        });
        doc.save(`${fileName}.pdf`);
    }
  };
  
  // --- Rule & Categorization Logic ---
  const handleOpenRuleModal = (rule: Rule | null) => {
    setEditingRule(rule);
    setIsRuleModalOpen(true);
  };

  const handleSaveRule = async (ruleData: Omit<Rule, 'id'>, id?: string) => {
      if (!currentUser) return;
      if (id) {
          const updatedRules = settings.rules.map(r => r.id === id ? { ...r, ...ruleData } : r);
          await firestoreService.updateSettings(currentUser.uid, { rules: updatedRules });
      } else {
          const newRule: Rule = { ...ruleData, id: `rule-${Date.now()}`, type: 'Categorization' };
          const updatedRules = [...settings.rules, newRule];
          await firestoreService.updateSettings(currentUser.uid, { rules: updatedRules });
      }
      setIsRuleModalOpen(false);
      setEditingRule(null);
  };

  const createGeneralizedRuleFromTransaction = async (transaction: Transaction, newCategory: string) => {
      if (!currentUser || !aiEnabled) return;
      const concept = transaction.concept.trim();
      if (!concept || newCategory === "Desconocida" || concept.toLowerCase() === 'n/a') {
          return;
      }

      try {
          const response = await geminiProxy.generateContent(
              `Analiza este concepto de transacción: "${concept}". Extrae la palabra o frase clave más distintiva y reutilizable para crear una regla de categorización. Por ejemplo, de "COMPRA AMAZON AMZN.COM/BILL MADRID" deberías extraer "AMAZON". De "PAGO GOOGLE *ADS 12345" deberías extraer "GOOGLE *ADS". De "Recibo Alquiler Piso" deberías extraer "Alquiler Piso". Devuelve solo la palabra clave en formato JSON {"keyword": "palabra_clave"}.`,
              {
                  responseMimeType: "application/json",
                  responseSchema: {
                      type: Type.OBJECT,
                      properties: {
                          keyword: {
                              type: Type.STRING,
                              description: "The extracted keyword or phrase.",
                          }
                      },
                      required: ["keyword"],
                  }
              },
          );
          
          const jsonText = response.text.trim();
          const jsonResponse = JSON.parse(jsonText);
          const keyword = jsonResponse.keyword?.trim();

          if (keyword) {
              const ruleExists = settings.rules.some(rule => rule.concept.trim().toLowerCase() === keyword.toLowerCase());
              if (!ruleExists) {
                  const newRule: Rule = {
                      id: `rule-${Date.now()}`,
                      type: 'Categorization',
                      concept: keyword, // Store the generalized keyword
                      category: newCategory,
                  };
                  const updatedRules = [newRule, ...settings.rules];
                  await firestoreService.updateSettings(currentUser.uid, { rules: updatedRules });
              }
          }
      } catch (error) {
          captureError(error, { action: "ai_rule_creation" });
      }
  };

  const handleConfirmCategory = async (transactionId: string, newCategory: string) => {
    if (!currentUser) return;
    const transaction = transactions.find(t => t.id === transactionId);
    if (!transaction) return;

    if (newCategory && !settings.categories.includes(newCategory)) {
        await handleAddCategory(newCategory);
    }
    
    const conceptLower = transaction.concept.toLowerCase();
    const commonPrefixes = ["venta de ", "venta ", "compra de ", "compra ", "pago ", "recibo ", "publicidad "];
    const matchingPrefix = commonPrefixes.find(p => conceptLower.startsWith(p));
    
    let idsToUpdate = [transactionId];

    if (matchingPrefix) {
        const similarUncategorized = transactions.filter(t => 
            t.id !== transactionId &&
            t.concept.toLowerCase().startsWith(matchingPrefix) &&
            t.category.includes('Desconocida')
        );
        idsToUpdate.push(...similarUncategorized.map(t => t.id));
    } else {
        const similarUncategorized = transactions.filter(t => 
            t.id !== transactionId && 
            t.concept === transaction.concept && 
            t.category.includes('Desconocida')
        );
        idsToUpdate.push(...similarUncategorized.map(t => t.id));
    }
    
    await handleBulkConfirmCategory(idsToUpdate, newCategory);
  };

  const handleBulkConfirmCategory = async (transactionIds: string[], newCategory: string) => {
    if (!currentUser) return;
    if (newCategory && !settings.categories.includes(newCategory)) {
        await handleAddCategory(newCategory);
    }
    
    if (transactionIds.length > 0) {
        const firstTransaction = transactions.find(t => t.id === transactionIds[0]);
        if (firstTransaction) {
            await createGeneralizedRuleFromTransaction(firstTransaction, newCategory);
        }
    }

    const updates = transactionIds.map(id => {
        return firestoreService.updateDocument(currentUser.uid, 'transactions', id, { category: [newCategory] });
    });
    
    await Promise.all(updates);
  };
  
  const handleDeleteRule = async (ruleId: string) => {
      if (!currentUser) return;
      const updatedRules = settings.rules.filter(rule => rule.id !== ruleId);
      await firestoreService.updateSettings(currentUser.uid, { rules: updatedRules });
  };
  
  const handleAddCategory = async (newCategory: string) => {
      if (!currentUser) return;
      if (newCategory && !settings.categories.includes(newCategory)) {
          const updatedCategories = [...settings.categories, newCategory].sort();
          await firestoreService.updateSettings(currentUser.uid, { categories: updatedCategories });
      }
  };

  // --- Proration Rule Handlers ---
  const handleSaveProrationRule = async (ruleData: Omit<ProrationRule, 'id'>, id?: string) => {
      if (!currentUser) return;
      if (id) {
          const updatedRules = settings.prorationRules.map(r => r.id === id ? { ...ruleData, id } : r);
          await firestoreService.updateSettings(currentUser.uid, { prorationRules: updatedRules });
      } else {
          const newRule = { ...ruleData, id: `proration-${Date.now()}`};
          const updatedRules = [...settings.prorationRules, newRule];
          await firestoreService.updateSettings(currentUser.uid, { prorationRules: updatedRules });
      }
  };

  const handleDeleteProrationRule = async (ruleId: string) => {
      if (!currentUser) return;
      const updatedRules = settings.prorationRules.filter(r => r.id !== ruleId);
      await firestoreService.updateSettings(currentUser.uid, { prorationRules: updatedRules });
  };


  // --- Time Log Handlers ---
  const handleAddTimeEntry = async (entryData: Omit<TimeEntry, 'id'>) => {
      if (!currentUser) return;
      await firestoreService.addDocument(currentUser.uid, 'timeEntries', entryData);
  };

  const handleUpdateTimeEntry = async (id: string, entryData: Omit<TimeEntry, 'id'>) => {
    if (!currentUser) return;
    await firestoreService.updateDocument(currentUser.uid, 'timeEntries', id, entryData);
  };

  const handleDeleteTimeEntry = async (id: string) => {
    if (!currentUser) return;
    await firestoreService.deleteDocument(currentUser.uid, 'timeEntries', id);
  };

  const handleBulkUpdateTimeEntries = async (ids: string[], dataToUpdate: Partial<Omit<TimeEntry, 'id'>>) => {
      if (!currentUser) return;
      
      const newCategory = dataToUpdate.category;
      if (newCategory && !settings.categories.includes(newCategory)) {
          await handleAddCategory(newCategory);
      }
      
      const updates = ids.map(id => {
          return firestoreService.updateDocument(currentUser.uid, 'timeEntries', id, dataToUpdate);
      });
      
      await Promise.all(updates);
  };

  const handleBulkDeleteTimeEntries = async (ids: string[]) => {
      if (!currentUser) return;
      
      const deletes = ids.map(id => {
          return firestoreService.deleteDocument(currentUser.uid, 'timeEntries', id);
      });

      await Promise.all(deletes);
  };
  
  // --- Profile Handlers ---
  const handleUpdateProfile = async (newAlias: string): Promise<void> => {
    // Use auth.currentUser to ensure we have the latest user object.
    if (auth.currentUser) {
        await auth.currentUser.updateProfile({ displayName: newAlias });
        // Manually update the state to reflect the change immediately in the UI.
        // This is a good practice when onAuthStateChanged might have a slight delay.
        const user = auth.currentUser;
        await user.reload(); // Get latest data from Firebase auth backend.
        setCurrentUser(auth.currentUser as firebase.User);
    }
  };

  const handleChangePassword = async (newPassword: string): Promise<void> => {
      if (currentUser) {
          await (currentUser as any).updatePassword(newPassword);
      }
  };
  
  const handleUpdateCurrency = async (newCurrency: string) => {
    if (!currentUser) return;
    await firestoreService.updateSettings(currentUser.uid, { currency: newCurrency });
  };

  const handleSetCategories = async (newCategories: string[]) => {
      if (!currentUser) return;
      await firestoreService.updateSettings(currentUser.uid, { categories: newCategories });
  };
  
  const handleKpiCardClick = (filter: 'income' | 'expense' | 'all') => {
    setInitialTransactionFilter(filter);
    handlePageChange('transactions');
  };

  const handleDisableTour = async () => {
    if (!currentUser) return;
    await firestoreService.updateSettings(currentUser.uid, { showTourGuide: false });
  };
  
  const handleBulkUpdateTransactions = async (ids: string[], dataToUpdate: Partial<Omit<Transaction, 'id'|'balance'>>) => {
      if (!currentUser) return;

      const newCategory = (dataToUpdate.category as string[]|undefined)?.[0];
      if (newCategory && !settings.categories.includes(newCategory)) {
        await handleAddCategory(newCategory);
      }

      const updates = ids.map(id => {
          return firestoreService.updateDocument(currentUser.uid, 'transactions', id, dataToUpdate);
      });

      await Promise.all(updates);
  };

  const handleBulkDeleteTransactions = async (ids: string[]) => {
      if (!currentUser) return;
      
      const deletes = ids.map(id => {
          return firestoreService.deleteDocument(currentUser.uid, 'transactions', id);
      });

      await Promise.all(deletes);
  };
  
  const handleAddClick = () => {
    switch (currentPage) {
        case 'dues':
            handleOpenAddDueModal();
            break;
        case 'subscriptions':
            handleOpenAddSubscriptionModal();
            break;
        case 'transactions':
        default:
            handleOpenAddModal();
            break;
    }
  };

  const pageTitles: Record<Page, string> = {
    dashboard: "Panel",
    transactions: "Transacciones",
    dues: "Vencimientos",
    subscriptions: "Suscripciones",
    "monthly-analysis": "Análisis Mensual",
    "time-tracking": "Registro de Horas",
    categories: "Categorías",
    settings: "Configuración",
    help: "Ayuda y Soporte",
    suggestions: "Enviar Sugerencias"
  };


  const renderContent = () => {
    const uncategorizedTransactions = transactions.filter(t => t.category.includes('Desconocida'));
    
    const effectiveTheme = theme === 'system' 
        ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light') 
        : theme;

    if (isReviewingCategorization) {
        return <CategorizationView 
                    transactionsToReview={uncategorizedTransactions}
                    categories={settings.categories}
                    onConfirmCategory={handleConfirmCategory}
                    onBulkConfirmCategory={handleBulkConfirmCategory}
                    onFinishReview={() => setIsReviewingCategorization(false)}
                    getCategoryFromAI={getCategoryFromAI}
                    onAddCategory={handleAddCategory}
                />;
    }

    switch (currentPage) {
      case 'dashboard':
        return <DashboardView 
                    transactions={transactions} 
                    dues={dues}
                    subscriptions={subscriptions}
                    categories={settings.categories} 
                    timeEntries={timeEntries}
                    uncategorizedCount={uncategorizedTransactions.length}
                    onStartReview={() => setIsReviewingCategorization(true)}
                    prorationRules={settings.prorationRules}
                    theme={effectiveTheme}
                    onKpiCardClick={handleKpiCardClick}
                    currency={settings.currency || 'EUR'}
                />;
      case 'transactions':
        return <DataView 
                    title="Transacciones"
                    description="Aquí puedes ver, editar y eliminar todas tus transacciones."
                    columns={[
                        { key: 'date', name: "Fecha" },
                        { key: 'concept', name: "Concepto" },
                        { key: 'counterparty', name: "Contrapartida" },
                        { key: 'amount', name: "Importe" },
                        { key: 'category', name: "Categorías" },
                        { key: 'paymentMethod', name: "Método de Pago" },
                    ]}
                    data={[...transactions].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())} 
                    onEdit={handleOpenEditModal}
                    onDelete={handleDeleteTransaction}
                    categories={settings.categories}
                    initialFilter={initialTransactionFilter}
                    clearInitialFilter={() => setInitialTransactionFilter(null)}
                    currency={settings.currency || 'EUR'}
                    user={currentUser}
                    onBulkUpdate={handleBulkUpdateTransactions}
                    onBulkDelete={handleBulkDeleteTransactions}
                />;
      case 'dues':
        return <DuesView
                    dues={dues}
                    onEdit={handleOpenEditDueModal}
                    onDelete={handleDeleteDue}
                    currency={settings.currency || 'EUR'}
                />;
      case 'subscriptions':
        return <SubscriptionsView
                    subscriptions={subscriptions}
                    onEdit={handleOpenEditSubscriptionModal}
                    onDelete={handleDeleteSubscription}
                    currency={settings.currency || 'EUR'}
                />;
      case 'monthly-analysis':
        return <MonthlyAnalysisView transactions={transactions} theme={effectiveTheme} currency={settings.currency || 'EUR'} />;
      case 'time-tracking':
        return <TimeLogView 
                    timeEntries={[...timeEntries].sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime())}
                    categories={settings.categories}
                    onAddTimeEntry={handleAddTimeEntry}
                    onUpdateTimeEntry={handleUpdateTimeEntry}
                    onDeleteTimeEntry={handleDeleteTimeEntry}
                    user={currentUser}
                    onBulkUpdate={handleBulkUpdateTimeEntries}
                    onBulkDelete={handleBulkDeleteTimeEntries}
                />;
      case 'categories':
        return <CategoryView categories={settings.categories} setCategories={handleSetCategories} />;
      case 'settings':
        return <SettingsView 
                    rules={settings.rules} 
                    onDeleteRule={handleDeleteRule}
                    onOpenRuleModal={handleOpenRuleModal}
                    prorationRules={settings.prorationRules}
                    onSaveProrationRule={handleSaveProrationRule}
                    onDeleteProrationRule={handleDeleteProrationRule}
                    categories={settings.categories}
                    onSaveRule={handleSaveRule}
                />;
      case 'help':
        return <HelpView ai={aiEnabled} />;
      case 'suggestions':
        return <SuggestionsView sections={Object.values(pageTitles)} currentUser={currentUser} />;
      default:
        return <DashboardView 
                  transactions={transactions}
                  dues={dues} 
                  subscriptions={subscriptions}
                  categories={settings.categories} 
                  timeEntries={timeEntries}
                  uncategorizedCount={uncategorizedTransactions.length}
                  onStartReview={() => setIsReviewingCategorization(true)}
                  prorationRules={settings.prorationRules}
                  theme={effectiveTheme}
                  onKpiCardClick={handleKpiCardClick}
                  currency={settings.currency || 'EUR'}
              />;
    }
  };
  
  

  if (firebaseError) {
    return (
      <div className="flex items-center justify-center h-screen bg-light-background dark:bg-background text-light-text-primary dark:text-text-primary p-8">
        <div className="text-center bg-light-surface dark:bg-surface p-8 rounded-lg border border-light-border dark:border-border max-w-2xl shadow-2xl">
          <h1 className="text-2xl font-bold text-red-500 mb-4">Error de Configuración de Firebase</h1>
          <p className="text-light-text-secondary dark:text-text-secondary whitespace-pre-wrap">{firebaseError}</p>
          <p className="mt-4 text-sm text-gray-500">
            Por favor, asegúrate de haber configurado correctamente tus credenciales de Firebase en el archivo <code>services/firebase.ts</code>.
          </p>
        </div>
      </div>
    );
  }

  if (loadingAuth) {
    return (
      <div className="flex items-center justify-center h-screen bg-light-background dark:bg-background text-light-text-primary dark:text-text-primary">
        Cargando...
      </div>
    );
  }

  if (!currentUser) {
    return <LoginView />;
  }

  if (!currentUser.emailVerified) {
    return <VerificationView user={currentUser} />;
  }
  
  if (!settings.onboardingCompleted) {
    return <OnboardingWizard onComplete={handleOnboardingComplete} />;
  }
  
  if (accessStatus === 'loading') {
    return (
      <div className="flex items-center justify-center h-screen bg-light-background dark:bg-background text-light-text-primary dark:text-text-primary">
        Verificando suscripción...
      </div>
    );
  }

  if (accessStatus !== 'granted') {
    const wallStatus = accessStatus.replace('denied_', '') as 'trial_ended' | 'canceled' | 'past_due';
    return <SubscriptionWall status={wallStatus} />;
  }


  return (
    <>
      {settings.subscriptionStatus === 'trial' && settings.trialEndDate && (
        <TrialBanner 
            trialEndDate={settings.trialEndDate} 
            onOpenPricingModal={() => setIsPricingModalOpen(true)} 
        />
      )}
      {showTour && <TourGuide onComplete={() => setShowTour(false)} onDisableTour={handleDisableTour} />}
      <VoiceCaptureModal 
        isOpen={isVoiceModalOpen}
        onClose={() => setIsVoiceModalOpen(false)}
        onAccept={handleVoiceAccept}
      />
      <TransactionModal 
          isOpen={isTransactionModalOpen}
          onClose={handleCloseTransactionModal}
          onSave={handleSaveTransaction}
          categories={settings.categories}
          transactionToEdit={editingTransaction}
          ai={aiEnabled}
          currency={settings.currency || 'EUR'}
          textForAiProcessing={voiceInputText}
          onAiProcessingComplete={() => setVoiceInputText(null)}
      />
      <DuesModal
        isOpen={isDuesModalOpen}
        onClose={handleCloseDueModal}
        onSave={handleSaveDue}
        categories={settings.categories}
        dueToEdit={editingDue}
        ai={aiEnabled}
        currency={settings.currency || 'EUR'}
        textForAiProcessing={voiceInputTextForDue}
        onAiProcessingComplete={() => setVoiceInputTextForDue(null)}
      />
      <SubscriptionModal
        isOpen={isSubscriptionModalOpen}
        onClose={handleCloseSubscriptionModal}
        onSave={handleSaveSubscription}
        categories={settings.categories}
        subscriptionToEdit={editingSubscription}
        ai={aiEnabled}
        currency={settings.currency || 'EUR'}
        textForAiProcessing={voiceInputTextForSubscription}
        onAiProcessingComplete={() => setVoiceInputTextForSubscription(null)}
      />
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={handleCloseExportModal}
        onExport={handleExportData}
        format={exportFormat}
      />
      <ProfileModal 
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        user={currentUser}
        onUpdateProfile={handleUpdateProfile}
        onChangePassword={handleChangePassword}
        onOpenDeleteAccountModal={() => {
            setIsProfileModalOpen(false);
            setIsDeleteAccountModalOpen(true);
        }}
        onOpenPricingModal={() => setIsPricingModalOpen(true)}
        onUpdateCurrency={handleUpdateCurrency}
        transactions={transactions}
        settings={settings}
      />
       <RuleModal
            isOpen={isRuleModalOpen}
            onClose={() => setIsRuleModalOpen(false)}
            onSave={handleSaveRule}
            categories={settings.categories}
            ruleToEdit={editingRule}
        />
      <LegalModal
        isOpen={isLegalModalOpen}
        onClose={() => setIsLegalModalOpen(false)}
        title={legalModalContent?.title || ''}
      >
        {legalModalContent?.content}
      </LegalModal>
      <DeleteAccountModal 
        isOpen={isDeleteAccountModalOpen}
        onClose={() => setIsDeleteAccountModalOpen(false)}
        user={currentUser}
      />
      <PricingModal
        isOpen={isPricingModalOpen}
        onClose={() => setIsPricingModalOpen(false)}
      />
      <VersionModal
        isOpen={isVersionModalOpen}
        onClose={() => setIsVersionModalOpen(false)}
      />
      <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
          accept=".xlsx, .xls"
      />
      <div className="flex h-screen bg-light-background dark:bg-background text-light-text-primary dark:text-text-primary">
        <Sidebar 
          currentPage={currentPage} 
          setCurrentPage={handlePageChange} 
          isOpen={isSidebarOpen} 
          toggleSidebar={toggleSidebar} 
          onOpenLegalModal={handleOpenLegalModal}
          onOpenVersionModal={() => setIsVersionModalOpen(true)}
        />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header 
            title={isReviewingCategorization ? "Centro de Categorización" : pageTitles[currentPage]} 
            user={currentUser}
            onAddClick={handleAddClick}
            onImportClick={handleTriggerImport}
            isImporting={isCategorizing}
            onExportClick={handleOpenExportModal}
            toggleSidebar={toggleSidebar}
            onOpenProfileModal={() => setIsProfileModalOpen(true)}
            onOpenLegalModal={handleOpenLegalModal}
            theme={theme}
            setTheme={setTheme}
            onVoiceCaptureClick={() => setIsVoiceModalOpen(true)}
          />
          <main className="flex-1 overflow-x-hidden overflow-y-auto bg-light-background dark:bg-background p-4 sm:p-6 lg:p-8">
            {apiKeyError && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md mb-6 dark:bg-red-900/30 dark:text-red-300 dark:border-red-600" role="alert">
                    <p className="font-bold">Error de Configuración de IA</p>
                    <p>{apiKeyError}</p>
                </div>
            )}
            {renderContent()}
          </main>
        </div>
      </div>
    </>
  );
};

export default App;